import clsx from 'clsx';
import { useRef } from 'react';
import { useButton } from 'react-aria';
import { useIntl } from 'react-intl';

import { SgComponentIcon } from '../../../display/ComponentIcon';
import { useDatePickerCalendar } from '../Calendar/CalendarContext';
import { useDatePickerConfig } from '../contexts/ConfigContext';

type CalendarView = 'day' | 'month' | 'year';

interface NavigationHeaderProps {
  view: CalendarView;
  onViewChange: (view: CalendarView) => void;
  onLastNavigationButtonKeyDown: (e: React.KeyboardEvent) => void;
}

export function DropdownNavigation({ view, onViewChange, onLastNavigationButtonKeyDown }: NavigationHeaderProps) {
  const { formatMessage } = useIntl();
  const { yearControls } = useDatePickerConfig();
  const { calendarState, title, prevButtonProps, nextButtonProps } = useDatePickerCalendar();

  const prevButtonRef = useRef<HTMLButtonElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);

  const { buttonProps: prevButton } = useButton(prevButtonProps, prevButtonRef);
  const { buttonProps: nextButton } = useButton(nextButtonProps, nextButtonRef);

  const handlePreviousYear = () => {
    const currentYear = calendarState.focusedDate.year;
    calendarState.setFocusedDate(calendarState.focusedDate.set({ year: currentYear - 1 }));
  };

  const handleNextYear = () => {
    const currentYear = calendarState.focusedDate.year;
    calendarState.setFocusedDate(calendarState.focusedDate.set({ year: currentYear + 1 }));
  };

  const handlePreviousYearRange = () => {
    const currentYear = calendarState.focusedDate.year;
    calendarState.setFocusedDate(calendarState.focusedDate.set({ year: currentYear - 12 }));
  };

  const handleNextYearRange = () => {
    const currentYear = calendarState.focusedDate.year;
    calendarState.setFocusedDate(calendarState.focusedDate.set({ year: currentYear + 12 }));
  };

  return (
    <div className="d-flex justify-content-between align-items-center">
      {view === 'day' && (
        <>
          {yearControls && (
            <button
              onClick={handlePreviousYear}
              type="button"
              className="btn btn-flat-primary btn-icon btn-lg"
              aria-label={formatMessage({ id: 'sgds.date.previousYear' })}
            >
              <SgComponentIcon icon="first_page" size="lg" data-e2e="first_page" />
            </button>
          )}
          <button
            {...prevButton}
            ref={prevButtonRef}
            type="button"
            className="btn btn-flat-primary btn-icon btn-lg"
            aria-label={formatMessage({ id: 'sgds.date.previousMonth' })}
          >
            <SgComponentIcon icon="keyboard_arrow_left" size="lg" data-e2e="keyboard_arrow_left" />
          </button>

          <div className="flex-grow-1 text-center">
            <button
              onClick={() => onViewChange('month')}
              className={clsx('btn btn-default text-capitalize fw-medium fs-4')}
              aria-label={title}
              type="button"
            >
              {title}
            </button>
          </div>

          <button
            {...nextButton}
            ref={nextButtonRef}
            type="button"
            className="btn btn-flat-primary btn-icon btn-lg"
            aria-label={formatMessage({ id: 'sgds.date.nextMonth' })}
            onKeyDown={yearControls ? undefined : onLastNavigationButtonKeyDown}
          >
            <SgComponentIcon icon="keyboard_arrow_right" size="lg" data-e2e="keyboard_arrow_right" />
          </button>
          {yearControls && (
            <button
              onClick={handleNextYear}
              onKeyDown={onLastNavigationButtonKeyDown}
              type="button"
              className="btn btn-flat-primary btn-icon btn-lg"
              aria-label={formatMessage({ id: 'sgds.date.nextYear' })}
            >
              <SgComponentIcon icon="last_page" size="lg" data-e2e="last_page" />
            </button>
          )}
        </>
      )}

      {view === 'month' && (
        <>
          <button
            onClick={handlePreviousYear}
            type="button"
            className="btn btn-flat-primary btn-icon btn-lg"
            aria-label={formatMessage({ id: 'sgds.date.previousYear' })}
          >
            <SgComponentIcon icon="keyboard_arrow_left" size="lg" data-e2e="keyboard_arrow_left" />
          </button>

          <div className="flex-grow-1 text-center">
            <button
              onClick={() => onViewChange('year')}
              className={clsx('btn btn-default text-capitalize fw-medium fs-4')}
              type="button"
            >
              {calendarState.focusedDate.year}
            </button>
          </div>

          <button
            onClick={handleNextYear}
            onKeyDown={onLastNavigationButtonKeyDown}
            type="button"
            className="btn btn-flat-primary btn-icon btn-lg"
            aria-label={formatMessage({ id: 'sgds.date.nextYear' })}
          >
            <SgComponentIcon icon="keyboard_arrow_right" size="lg" data-e2e="keyboard_arrow_right" />
          </button>
        </>
      )}

      {view === 'year' && (
        <>
          <button
            onClick={handlePreviousYearRange}
            type="button"
            className="btn btn-flat-primary btn-icon btn-lg"
            aria-label={formatMessage({ id: 'sgds.date.previousYear' })}
          >
            <SgComponentIcon icon="keyboard_arrow_left" size="lg" data-e2e="keyboard_arrow_left" />
          </button>

          <button
            onClick={handleNextYearRange}
            onKeyDown={onLastNavigationButtonKeyDown}
            type="button"
            className="btn btn-flat-primary btn-icon btn-lg"
            aria-label={formatMessage({ id: 'sgds.date.nextYear' })}
          >
            <SgComponentIcon icon="keyboard_arrow_right" size="lg" data-e2e="keyboard_arrow_right" />
          </button>
        </>
      )}
    </div>
  );
}
